import * as React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { COMMUNITY_TERMS, TERMS_SUMMARY } from '@sixam/shared/community-terms';
import { recordTermsAcceptance } from '@/lib/community-terms-client';
import { supabase } from '@sixam/supabase/client';

/**
 * Blocks the app until the member has agreed to the community terms.
 *
 * Sign-up has its own checkbox, but that only ever covers new accounts. Everyone
 * who already had one — including any account App Review is given — would
 * otherwise reach the app having agreed to nothing, which is precisely what
 * guideline 1.2 asks not to happen: the terms must be presented before
 * registering *or logging in*.
 *
 * There is no "skip". Agreeing or signing out are the only ways past.
 */
export function TermsGate({ onAccepted }: Readonly<{ onAccepted: () => void }>) {
  const insets = useSafeAreaInsets();
  const [isWorking, setIsWorking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function accept() {
    setIsWorking(true);
    setError(null);
    try {
      await recordTermsAcceptance();
      onAccepted();
    } catch (acceptError) {
      setError(
        acceptError instanceof Error ? acceptError.message : 'Could not save your agreement.'
      );
      setIsWorking(false);
    }
  }

  return (
    <View className="flex-1 bg-background">
      <ScrollView
        contentContainerStyle={{
          paddingBottom: insets.bottom + 24,
          paddingHorizontal: 20,
          paddingTop: insets.top + 24,
        }}
      >
        <View className="mx-auto w-full max-w-3xl gap-6">
          <View className="gap-2">
            <Text variant="h2">Before you continue</Text>
            <Text variant="muted">{TERMS_SUMMARY}</Text>
          </View>

          {COMMUNITY_TERMS.map((section) => (
            <View className="gap-2" key={section.title}>
              <Text variant="large">{section.title}</Text>
              {section.body.map((paragraph) => (
                <Text key={paragraph} selectable>
                  {paragraph}
                </Text>
              ))}
            </View>
          ))}

          {error ? (
            <Text className="text-sm text-destructive" selectable>
              {error}
            </Text>
          ) : null}

          <View className="gap-3">
            <Button disabled={isWorking} onPress={() => void accept()}>
              {isWorking ? <ActivityIndicator color="white" /> : null}
              <Text>{isWorking ? 'Saving…' : 'I agree'}</Text>
            </Button>
            <Button
              disabled={isWorking}
              onPress={() => void supabase.auth.signOut()}
              variant="secondary"
            >
              <Text>Sign out</Text>
            </Button>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
